import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext, KeyboardSensor, MouseSensor, TouchSensor,
  useSensor, useSensors, DragOverlay, pointerWithin, rectIntersection, closestCenter,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext,
  sortableKeyboardCoordinates, rectSortingStrategy,
} from '@dnd-kit/sortable';
import GlowInput from './components/GlowInput';
import GridItem from './components/GridItem';
import SortableGridItem from './components/SortableGridItem';
import MoveToModal from './components/MoveToModal';
import EditModal from './components/EditModal';
import AddBookmarkModal from './components/AddBookmarkModal';
import AddFolderModal from './components/AddFolderModal';
import SettingsModal from './components/SettingsModal';
import AuthPage from './components/AuthPage';
import ContextMenu from './components/ContextMenu';
import AIAssistant from './components/AIAssistant';
import AIButton from './components/AIButton';
import BookmarkInfoPanel from './components/BookmarkInfoPanel';
import AdminPanel from './components/AdminPanel';
import ShareFolderModal from './components/ShareFolderModal';
import CommandPalette from './components/CommandPalette';
import CyberTerminalModal from './components/CyberTerminalModal';
import GraphViewModal from './components/GraphViewModal';
import ScratchpadDrawer from './components/ScratchpadDrawer';
import CodeRunnerModal from './components/CodeRunnerModal';
import RssFeedDrawer from './components/RssFeedDrawer';
import TotpModal from './components/TotpModal';
import ActivityHeatmapModal from './components/ActivityHeatmapModal';
import RegexRulesModal from './components/RegexRulesModal';
import PdfViewerModal from './components/PdfViewerModal';
import SessionSnapshotModal from './components/SessionSnapshotModal';
import { getCircadianPhase, isCircadianEnabled, applyCircadianTheme } from './lib/ambientChronos';
import { isPdfUrl } from './lib/pdfDetector';
import { findStaleBookmarks } from './lib/staleArchiver';
import { downloadObsidianVault } from './lib/obsidianExporter';
import { downloadStandaloneVault } from './lib/standaloneExporter';
import NetworkStatusPill from './components/NetworkStatusPill';
import QuickAccessDock from './components/QuickAccessDock';
import AISummaryModal from './components/AISummaryModal';
import QRCodeModal from './components/QRCodeModal';
import AuditModal from './components/AuditModal';
import VaultSecurityModal from './components/VaultSecurityModal';
import ReadingListDrawer from './components/ReadingListDrawer';
import KanbanBoard from './components/KanbanBoard';
import BatchActionBar from './components/BatchActionBar';
import KeyboardShortcutsModal from './components/KeyboardShortcutsModal';
import TagFilterBar from './components/TagFilterBar';
import FolderScratchpadModal from './components/FolderScratchpadModal';
import { exportToJSON, exportToHTML } from './lib/bookmarkIO';
import { playSound, toggleSound, isSoundMuted } from './lib/soundEffects';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getSession, signOut, shareFolder } from './api';
import { useBookmarkData } from './hooks/useBookmarkData';

import ConstellationBg from './components/backgrounds/ConstellationBg';
import AuroraBg from './components/backgrounds/AuroraBg';
import FirefliesBg from './components/backgrounds/FirefliesBg';
import RippleBg from './components/backgrounds/RippleBg';
import CosmicBg from './components/backgrounds/CosmicBg';
import BlobBg from './components/backgrounds/BlobBg';
import MatrixBg from './components/backgrounds/MatrixBg';
import { getEnabledFeatures } from './lib/featureFlags';

const {
  FiArrowLeft, FiLoader, FiClock, FiXCircle,
  FiCommand, FiGrid, FiList, FiStar, FiExternalLink, FiShare2,
  FiFolder, FiBookmark, FiShield, FiBookOpen, FiColumns,
  FiVolume2, FiVolumeX, FiMaximize2, FiMinimize2, FiHelpCircle,
  FiEdit3, FiCheckSquare, FiCheck, FiSettings, FiLogOut, FiSliders, FiCpu,
  FiTerminal, FiFileText, FiPlus
} = FiIcons;
const DRAG_ACTIVATION_DISTANCE = 8;

const BG_COMPONENTS = {
  constellation: ConstellationBg,
  aurora: AuroraBg,
  fireflies: FirefliesBg,
  ripple: RippleBg,
  cosmic: CosmicBg,
  blob: BlobBg,
  matrix: MatrixBg,
};

function GateCard({ icon, iconClass, iconBg, iconBorder, title, message, onSignOut }) {
  const handleClearAndSignOut = () => {
    signOut();
    localStorage.removeItem('ncb_token');
    localStorage.removeItem('ncb_user');
    if (onSignOut) onSignOut();
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0a0a0a]">
      <ConstellationBg accentColor="#10b981" isDark={true} pageBg="#0a0a0a" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-sm mx-4 text-center">
        <div className="mb-8"><h1 className="text-3xl font-bold text-white tracking-tight">MeMark</h1></div>
        <div className="bg-[#111]/90 backdrop-blur-xl border border-[#2a2a2a] rounded-2xl p-8 shadow-2xl">
          <div className={`w-14 h-14 rounded-full ${iconBg} ${iconBorder} border flex items-center justify-center mx-auto mb-4`}>
            <SafeIcon icon={icon} className={`w-6 h-6 ${iconClass}`} />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{title}</h2>
          <p className="text-[#a3a3a3] text-sm leading-relaxed mb-6">{message}</p>
          <button
            type="button"
            onClick={handleClearAndSignOut}
            className="w-full py-2.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs transition-all cursor-pointer shadow-lg shadow-emerald-500/20 active:scale-95"
          >
            Sign Out & Return to Gateway
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function RoleGate({ role, onSignOut }) {
  const handleSignOut = () => {
    signOut();
    localStorage.removeItem('ncb_token');
    localStorage.removeItem('ncb_user');
    if (onSignOut) onSignOut();
    window.location.reload();
  };

  if (role === 'pending') return (
    <GateCard icon={FiClock} iconClass="text-amber-400" iconBg="bg-amber-500/10" iconBorder="border-amber-500/30"
      title="Awaiting Approval" message="Your account is pending review by an admin. You'll get access once approved."
      onSignOut={handleSignOut} />
  );

  return (
    <GateCard icon={FiXCircle} iconClass="text-red-400" iconBg="bg-red-500/10" iconBorder="border-red-500/30"
      title="Access Denied" message="Your account request was not approved. Contact the administrator if you believe this is a mistake."
      onSignOut={handleSignOut} />
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────
function Dashboard({ user, initialRole, onSignOut }) {
  const {
    data, settings, loading: dataLoading, persistSettings,
    addBookmark, addFolder, editBookmark, editFolder,
    removeBookmark, removeFolder, moveBookmarkToFolder, moveFolderToFolder,
    groupBookmarksIntoNewFolder,
    reorderItems, loadAll,
  } = useBookmarkData(user);

  const isUserAdmin = user?.email?.toLowerCase() === 'y.yang.priv@gmail.com' || user?.role === 'admin';
  const effectiveRole = isUserAdmin ? 'admin' : (initialRole || (user?.isLocal ? 'local' : null));
  const [role, setRole] = useState(effectiveRole);
  const [roleLoading, setRoleLoading] = useState(!effectiveRole && !isUserAdmin);

  useEffect(() => {
    if (isUserAdmin) {
      setRole('admin');
      setRoleLoading(false);
      return;
    }
    if (effectiveRole) return;
    let cancelled = false;
    async function check() {
      try {
        const { getUserRole } = await import('./api');
        const r = await getUserRole(user.id, user.email);
        if (!cancelled) { setRole(r || 'approved'); setRoleLoading(false); }
      } catch {
        if (!cancelled) { setRole('approved'); setRoleLoading(false); }
      }
    }
    check();
    return () => { cancelled = true; };
  }, [user.id, user.email, effectiveRole, isUserAdmin]);

  const isLoading = dataLoading || roleLoading;
  const isAdmin = role === 'admin' || isUserAdmin;

  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [folderHistory, setFolderHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addBookmarkOpen, setAddBookmarkOpen] = useState(false);
  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('general');
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [shareFolderModal, setShareFolderModal] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [infoItem, setInfoItem] = useState(null);
  const [moveToItem, setMoveToItem] = useState(null);
  const [batchMoveItems, setBatchMoveItems] = useState([]);

  // New MeMark Feature States
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [scratchpadDrawerOpen, setScratchpadDrawerOpen] = useState(false);
  const [aiSummaryItem, setAiSummaryItem] = useState(null);
  const [qrCodeItem, setQrCodeItem] = useState(null);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('memark_view_mode') || 'grid');
  const [density, setDensity] = useState(() => localStorage.getItem('memark_grid_density') || 'standard');
  const [pinnedIds, setPinnedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memark_pinned_bookmarks') || '[]'); }
    catch { return []; }
  });

  const [codeRunnerState, setCodeRunnerState] = useState({ isOpen: false, code: '', title: '', language: 'javascript' });
  const [rssFeedBookmark, setRssFeedBookmark] = useState(null);
  const [totpBookmark, setTotpBookmark] = useState(null);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [regexRulesOpen, setRegexRulesOpen] = useState(false);
  const [pdfViewerBookmark, setPdfViewerBookmark] = useState(null);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [circadianPhase, setCircadianPhase] = useState(() => getCircadianPhase());

  useEffect(() => {
    if (isCircadianEnabled()) {
      const ph = getCircadianPhase();
      setCircadianPhase(ph);
      applyCircadianTheme(ph);
    }
    const timer = setInterval(() => {
      if (isCircadianEnabled()) {
        const ph = getCircadianPhase();
        setCircadianPhase(ph);
        applyCircadianTheme(ph);
      }
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleArchiveStale = useCallback(async () => {
    const all = [];
    Object.values(data).forEach(folder => {
      (folder.items || []).forEach(item => {
        if (item.type === 'link') all.push(item);
      });
    });
    const stale = findStaleBookmarks(all);
    for (const item of stale) {
      const curNote = item.note || '';
      const newNote = curNote ? `${curNote} #archive` : '#archive';
      await editBookmark(item.id, { note: newNote });
    }
    return stale.length;
  }, [data, editBookmark]);

  // Majestic & Security Feature States
  const [zenMode, setZenMode] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [vaultModalOpen, setVaultModalOpen] = useState(false);
  const [readingDrawerOpen, setReadingDrawerOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const [scratchpadFolder, setScratchpadFolder] = useState(null);
  // Sovereign Modular Feature Flags (all extra functions off by default)
  const [features, setFeatures] = useState(() => getEnabledFeatures());

  useEffect(() => {
    const handleFeaturesUpdate = (e) => {
      if (e?.detail) setFeatures(e.detail);
      else setFeatures(getEnabledFeatures());
    };
    window.addEventListener('memark_features_updated', handleFeaturesUpdate);
    return () => window.removeEventListener('memark_features_updated', handleFeaturesUpdate);
  }, []);

  useEffect(() => {
    const handleFilterView = (e) => {
      const detail = e?.detail || {};
      if (detail.reset) {
        setSelectedTag(null);
        setSearchQuery('');
        return;
      }
      if (detail.tag !== undefined && detail.tag !== null) {
        setSelectedTag(detail.tag);
      }
      if (detail.query !== undefined && detail.query !== null) {
        setSearchQuery(detail.query);
      }
      if (detail.folderTitle && data) {
        const found = Object.values(data).find(
          (f) => (f.title || '').toLowerCase() === detail.folderTitle.toLowerCase()
        );
        if (found) setCurrentFolderId(found.id);
      }
    };
    window.addEventListener('memark-filter-view', handleFilterView);
    return () => window.removeEventListener('memark-filter-view', handleFilterView);
  }, [data]);

  // Click frequency & Folder color states
  const [clickStats, setClickStats] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memark_click_stats') || '{}'); }
    catch { return {}; }
  });
  const [folderColors, setFolderColors] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memark_folder_colors') || '{}'); }
    catch { return {}; }
  });
  const [folderGradients, setFolderGradients] = useState(() => {
    try { return JSON.parse(localStorage.getItem('memark_folder_gradients') || '{}'); }
    catch { return {}; }
  });
  const [sortMode, setSortMode] = useState(() => localStorage.getItem('memark_sort_mode') || 'default');

  useEffect(() => {
    const handleSortModeUpdate = (e) => {
      const mode = e?.detail?.sortMode || localStorage.getItem('memark_sort_mode');
      if (mode) setSortMode(mode);
    };
    window.addEventListener('memark_sort_mode_updated', handleSortModeUpdate);
    return () => window.removeEventListener('memark_sort_mode_updated', handleSortModeUpdate);
  }, []);
  const [dockConfig, setDockConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('memark_dock_config');
      return saved ? JSON.parse(saved) : {
        settings: true, vault: true, reading: true, notes: true,
        graph: true, scratchpad: true, sound: true, zen: true, shortcuts: true,
      };
    } catch {
      return {
        settings: true, vault: true, reading: true, notes: true,
        graph: true, scratchpad: true, sound: true, zen: true, shortcuts: true,
      };
    }
  });

  useEffect(() => {
    const handleDockUpdate = () => {
      try {
        const saved = localStorage.getItem('memark_dock_config');
        if (saved) setDockConfig(JSON.parse(saved));
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('memark_dock_updated', handleDockUpdate);
    return () => window.removeEventListener('memark_dock_updated', handleDockUpdate);
  }, []);

  const dataRef = useRef(data);
  const currentFolderIdRef = useRef(currentFolderId);
  const displayItemsRef = useRef([]);
  const itemCtxHandledRef = useRef(false);

  useEffect(() => { dataRef.current = data; }, [data]);
  useEffect(() => { currentFolderIdRef.current = currentFolderId; }, [currentFolderId]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 6,
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const displayItems = useMemo(() => {
    let raw = [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      raw = Object.values(data).flatMap((f) => f.items || []).filter((item) => item.title?.toLowerCase().includes(q));
    } else {
      raw = data[currentFolderId]?.items || [];
    }

    const decorated = raw.map((item) => {
      const stats = clickStats[item.id] || {};
      const fColor = folderColors[item._dbFolderId || item.targetId || item.id] || item.color;
      const fGrad = folderGradients[item._dbFolderId || item.targetId || item.id] || item.gradient;
      return {
        ...item,
        clicks: stats.clicks || 0,
        lastVisited: stats.lastVisited || 0,
        color: fColor || item.color,
        gradient: fGrad || item.gradient,
      };
    });

    if (sortMode === 'clicks') {
      return [...decorated].sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
    }
    if (sortMode === 'recent') {
      return [...decorated].sort((a, b) => (b.lastVisited || 0) - (a.lastVisited || 0));
    }
    if (sortMode === 'alpha') {
      return [...decorated].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }
    if (sortMode === 'domain') {
      return [...decorated].sort((a, b) => {
        const dA = a.url ? (a.url.replace(/^https?:\/\//, '').split('/')[0] || '') : '';
        const dB = b.url ? (b.url.replace(/^https?:\/\//, '').split('/')[0] || '') : '';
        return dA.localeCompare(dB);
      });
    }
    return decorated;
  }, [data, currentFolderId, searchQuery, clickStats, folderColors, sortMode]);

  const filteredDisplayItems = useMemo(() => {
    if (!selectedTag) return displayItems;
    const tagLower = `#${selectedTag.toLowerCase()}`;
    return displayItems.filter((item) => {
      const searchTarget = `${item.title || ''} ${item.note || ''}`.toLowerCase();
      return searchTarget.includes(tagLower);
    });
  }, [displayItems, selectedTag]);

  useEffect(() => { displayItemsRef.current = filteredDisplayItems; }, [filteredDisplayItems]);

  const currentFolder = data[currentFolderId];
  const isInSharedFolder = currentFolder?._isShared;
  const currentFolderPermission = currentFolder?._sharedPermission || 'viewer';
  const currentFolderIsEditor = isInSharedFolder && currentFolderPermission === 'editor';
  const canMutateHere = !isInSharedFolder || currentFolderIsEditor;

  // Reading Queue Items across entire collection
  const readingListItems = useMemo(() => {
    return Object.values(data)
      .flatMap((f) => (f.items || []).filter((i) => i.type === 'link'))
      .filter((i) => i.isReadingList || i.readLater || i.note?.includes('#reading') || i.note?.includes('#readlater'));
  }, [data]);

  // Selected items for bulk operations
  const selectedItems = useMemo(() => {
    const all = Object.values(data).flatMap((f) => f.items || []);
    return selectedItemIds.map((id) => all.find((i) => i.id === id)).filter(Boolean);
  }, [data, selectedItemIds]);

  const handleToggleSelect = useCallback((item) => {
    playSound('click');
    setSelectedItemIds((prev) =>
      prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    playSound('click');
    const currentItems = displayItemsRef.current || [];
    setSelectedItemIds(currentItems.map((i) => i.id));
  }, []);

  const handleBatchLaunchAll = useCallback(() => {
    playSound('launch');
    selectedItems.forEach((i) => {
      if (i.url) window.open(i.url, '_blank');
    });
  }, [selectedItems]);

  const handleBatchExport = useCallback(() => {
    playSound('click');
    exportToJSON(selectedItems);
  }, [selectedItems]);

  const handleBatchDelete = useCallback(async () => {
    if (!window.confirm(`Delete ${selectedItems.length} selected items?`)) return;
    setSaving(true);
    try {
      for (const item of selectedItems) {
        if (item.type === 'folder') await removeFolder(item._dbFolderId || item.targetId);
        else await removeBookmark(item.id);
      }
      setSelectedItemIds([]);
    } finally {
      setSaving(false);
    }
  }, [selectedItems, removeFolder, removeBookmark]);

  const handleToggleSound = useCallback(() => {
    const next = toggleSound();
    setIsMuted(next);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const inInput = ['INPUT', 'TEXTAREA'].includes(e.target?.tagName);
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        playSound('click');
        setCommandPaletteOpen((prev) => !prev);
      } else if (e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        playSound('click');
        setZenMode((prev) => !prev);
      } else if (e.altKey && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        playSound('lock');
        setVaultModalOpen((prev) => !prev);
      } else if (e.altKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        playSound('click');
        setReadingDrawerOpen((prev) => !prev);
      } else if ((e.metaKey || e.ctrlKey || e.altKey) && e.key === ',') {
        e.preventDefault();
        playSound('click');
        setSettingsModalOpen((prev) => !prev);
      } else if (!inInput && (e.key === '?' || (e.shiftKey && e.key === '/'))) {
        e.preventDefault();
        playSound('click');
        setShortcutsModalOpen((prev) => !prev);
      } else if (e.altKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        playSound('launch');
        setGraphModalOpen((prev) => !prev);
      } else if (e.altKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        playSound('click');
        setScratchpadDrawerOpen((prev) => !prev);
      } else if (!inInput && (e.key === '`' || e.key === '~' || (e.ctrlKey && e.key === '`'))) {
        e.preventDefault();
        playSound('launch');
        setTerminalOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setGraphModalOpen(false);
        setScratchpadDrawerOpen(false);
        setTerminalOpen(false);
        setCommandPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTogglePin = useCallback((item) => {
    const id = item?.id || item;
    setPinnedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
      localStorage.setItem('memark_pinned_bookmarks', JSON.stringify(next));
      return next;
    });
  }, []);

  const pinnedBookmarks = useMemo(() => {
    const allBm = Object.values(data).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
    return pinnedIds.map((id) => allBm.find((b) => b.id === id)).filter(Boolean);
  }, [data, pinnedIds]);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const next = prev === 'grid' ? 'list' : prev === 'list' ? 'kanban' : 'grid';
      localStorage.setItem('memark_view_mode', next);
      return next;
    });
  }, []);

  const handleImportBookmarks = useCallback(async (parsed) => {
    setSaving(true);
    try {
      for (const bm of parsed.rootBookmarks || []) {
        await addBookmark('root', {
          title: bm.title,
          url: bm.url,
          logoUrl: `https://www.google.com/s2/favicons?domain=${new URL(bm.url).hostname}&sz=128`,
        });
      }
      for (const f of parsed.folders || []) {
        const newFolder = await addFolder(f.title, 'root');
        const folderId = newFolder?.id;
        if (folderId) {
          for (const bm of f.bookmarks || []) {
            await addBookmark(folderId, {
              title: bm.title,
              url: bm.url,
              logoUrl: `https://www.google.com/s2/favicons?domain=${new URL(bm.url).hostname}&sz=128`,
            });
          }
        }
      }
    } catch (err) {
      console.error('Import failed:', err);
    } finally {
      setSaving(false);
    }
  }, [addBookmark, addFolder]);

  const recordBookmarkClick = useCallback((item) => {
    if (!item?.id) return;
    setClickStats((prev) => {
      const next = {
        ...prev,
        [item.id]: {
          clicks: (prev[item.id]?.clicks || 0) + 1,
          lastVisited: Date.now(),
        },
      };
      localStorage.setItem('memark_click_stats', JSON.stringify(next));
      return next;
    });
  }, []);

  const handleOpenBookmark = useCallback((url, optionalItem) => {
    if (!url) return;
    if (optionalItem) {
      recordBookmarkClick(optionalItem);
    } else {
      const match = Object.values(dataRef.current).flatMap((f) => f.items || []).find((i) => i.url === url);
      if (match) recordBookmarkClick(match);
    }
    const absUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    window.open(absUrl, '_blank', 'noopener,noreferrer');
  }, [recordBookmarkClick]);

  useEffect(() => {
    const handleGlobalContextMenu = (e) => {
      if (itemCtxHandledRef.current) { itemCtxHandledRef.current = false; return; }
      const isModal = e.target.closest('[data-modal]');
      const isCtxMenu = e.target.closest('[data-ctxmenu]');
      const isAi = e.target.closest('[data-ai]');
      const isInfo = e.target.closest('[data-info]');
      if (isModal || isCtxMenu || isAi || isInfo) return;
      e.preventDefault();
      setCtxMenu({ x: e.clientX, y: e.clientY, item: null });
    };
    document.addEventListener('contextmenu', handleGlobalContextMenu);
    return () => document.removeEventListener('contextmenu', handleGlobalContextMenu);
  }, []);

  const handleContextMenu = useCallback((e, item) => {
    if (activeId) return;
    e.preventDefault(); e.stopPropagation();
    itemCtxHandledRef.current = true;
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || (window.innerWidth / 2);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || (window.innerHeight / 2);
    setCtxMenu({ x: clientX, y: clientY, item });
  }, [activeId]);

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);

  const handleConfirmMoveTo = useCallback(async (targetFolderId, itemsToMove) => {
    const items = itemsToMove && itemsToMove.length > 0
      ? itemsToMove
      : batchMoveItems.length > 0
      ? batchMoveItems
      : moveToItem
      ? [moveToItem]
      : [];
    if (items.length === 0) return;

    setSaving(true);
    try {
      for (const it of items) {
        if (it.type === 'folder') {
          const sourceFolderId = it.targetId || it._dbFolderId || it.id;
          await moveFolderToFolder(sourceFolderId, targetFolderId);
        } else {
          await moveBookmarkToFolder(it.id, targetFolderId);
        }
      }
      playSound('launch');
      setSelectedItemIds([]);
      setMoveToItem(null);
      setBatchMoveItems([]);
    } catch (err) {
      console.error('Error moving item(s) to folder:', err);
      alert('Failed to move: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }, [moveToItem, batchMoveItems, moveFolderToFolder, moveBookmarkToFolder]);

  const handleDragStart = useCallback((event) => {
    setCtxMenu(null);
    setActiveId(event.active.id);
    setOverId(null);
    if (navigator.vibrate) {
      try { navigator.vibrate(40); } catch (_) {}
    }
  }, []);

  const handleDragOver = useCallback((event) => {
    setOverId(event.over?.id ?? null);
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) return;
    const folderId = currentFolderIdRef.current;
    const folderItems = dataRef.current[folderId]?.items || [];
    const oldIndex = folderItems.findIndex((i) => i.id === active.id);
    const newIndex = folderItems.findIndex((i) => i.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      await reorderItems(folderId, arrayMove([...folderItems], oldIndex, newIndex));
    }
  }, [reorderItems]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setOverId(null);
  }, []);

  const handleNavigate = useCallback((targetId) => {
    if (targetId === 'root') { setCurrentFolderId('root'); setFolderHistory([]); return; }
    const folder = data[targetId];
    if (folder) {
      setCurrentFolderId(targetId);
      setFolderHistory((prev) => [...prev, { id: folder.id, title: folder.title }]);
    }
  }, [data]);

  const handleBack = useCallback(() => {
    setFolderHistory((prev) => {
      const next = prev.slice(0, -1);
      setCurrentFolderId(next.length > 0 ? next[next.length - 1].id : 'root');
      return next;
    });
  }, []);

  const handleItemClick = useCallback((item) => {
    if (selectedItemIds.length > 0) {
      handleToggleSelect(item);
      return;
    }
    if (item.type === 'folder') {
      setSearchQuery('');
      handleNavigate(item.targetId);
    } else if (item.type === 'link') {
      recordBookmarkClick(item);
      window.open(item.url, '_blank');
    }
  }, [handleNavigate, recordBookmarkClick, selectedItemIds, handleToggleSelect]);

  const handleCtxEdit = useCallback(() => {
    const item = ctxMenu?.item;
    if (!item) return;
    if (item._isShared && item._sharedPermission !== 'editor') return;
    if (item._isShared && item.type === 'folder') return;
    setEditingItem(item);
    setEditModalOpen(true);
  }, [ctxMenu]);

  const handleCtxDelete = useCallback(async () => {
    const item = ctxMenu?.item;
    if (!item) return;
    if (item._isShared && item._sharedPermission !== 'editor') return;
    if (item._isShared && item.type === 'folder') return;
    setSaving(true);
    try {
      if (item.type === 'folder') await removeFolder(item._dbFolderId || item.targetId);
      else await removeBookmark(item.id);
    } finally { setSaving(false); }
  }, [ctxMenu, removeFolder, removeBookmark]);

  const handleCtxMoveOut = useCallback(async () => {
    const item = ctxMenu?.item;
    if (!item || item.type !== 'link' || item._isShared) return;
    const parentId = folderHistory.length > 1 ? folderHistory[folderHistory.length - 2].id : 'root';
    setSaving(true);
    try { await moveBookmarkToFolder(item.id, parentId); }
    finally { setSaving(false); }
  }, [ctxMenu, folderHistory, moveBookmarkToFolder]);

  const handleCtxMoveFolderOut = useCallback(async () => {
    const item = ctxMenu?.item;
    if (!item || item.type !== 'folder') return;
    if (item._isShared && !item._memberRecordId) return;

    const grandParentId = folderHistory.length > 1
      ? folderHistory[folderHistory.length - 2].id
      : 'root';

    const folderId = item._dbFolderId || item.targetId;

    setSaving(true);
    try {
      await moveFolderToFolder(folderId, grandParentId);
    } catch (err) {
      console.error('Move folder out error:', err);
    } finally {
      setSaving(false);
    }
  }, [ctxMenu, folderHistory, moveFolderToFolder]);

  const handleCtxShare = useCallback(() => {
    const item = ctxMenu?.item;
    if (!item || item.type !== 'folder') return;
    setShareFolderModal(item);
  }, [ctxMenu]);

  const handleCtxLeaveFolder = useCallback(async () => {
    const item = ctxMenu?.item;
    if (!item || !item._isShared || item.type !== 'folder') return;
    setShareFolderModal(item);
  }, [ctxMenu]);

  const handleLeaveFolder = useCallback((folderId) => {
    setShareFolderModal(null);
    if (currentFolderId === folderId || currentFolderId.startsWith(`shared_${folderId}`)) {
      setCurrentFolderId('root');
      setFolderHistory([]);
    }
    loadAll(true);
  }, [currentFolderId, loadAll]);

  const handleSaveBookmark = useCallback(async (itemData) => {
    if (!canMutateHere) return;
    setSaving(true);
    try { await addBookmark(currentFolderIdRef.current, itemData); }
    finally { setSaving(false); setAddBookmarkOpen(false); }
  }, [addBookmark, canMutateHere]);

  const handleSaveFolder = useCallback(async (itemData) => {
    if (!canMutateHere) return;
    setSaving(true);
    try {
      const newFolder = await addFolder(currentFolderIdRef.current, itemData.title);
      if (itemData.color && newFolder?.id) {
        setFolderColors((prev) => {
          const next = { ...prev, [newFolder.id]: itemData.color };
          localStorage.setItem('memark_folder_colors', JSON.stringify(next));
          return next;
        });
      }
      if (itemData.gradient && newFolder?.id) {
        setFolderGradients((prev) => {
          const next = { ...prev, [newFolder.id]: itemData.gradient };
          localStorage.setItem('memark_folder_gradients', JSON.stringify(next));
          return next;
        });
      }
    } finally {
      setSaving(false);
      setAddFolderOpen(false);
    }
  }, [addFolder, canMutateHere]);

  const handleSaveEdit = useCallback(async (itemData) => {
    setSaving(true);
    try {
      if (editingItem?.type === 'folder') await editFolder(editingItem._dbFolderId || editingItem.targetId, itemData.title);
      else await editBookmark(editingItem.id, itemData);
    } finally { setSaving(false); setEditModalOpen(false); }
  }, [editingItem, editFolder, editBookmark]);

  const handleDeleteItem = useCallback(async (itemToDelete) => {
    if (itemToDelete._isShared && itemToDelete._sharedPermission !== 'editor') return;
    if (itemToDelete._isShared && itemToDelete.type === 'folder') return;
    setSaving(true);
    try {
      if (itemToDelete.type === 'folder') await removeFolder(itemToDelete._dbFolderId || itemToDelete.targetId);
      else await removeBookmark(itemToDelete.id);
    } finally { setSaving(false); setEditModalOpen(false); }
  }, [removeFolder, removeBookmark]);

  const handleInfoSave = useCallback(async (itemId, { note, credentials }) => {
    await editBookmark(itemId, { note, credentials });
    setInfoItem((prev) => prev ? { ...prev, note, credentials } : prev);
  }, [editBookmark]);

  const handleAIShareFolder = useCallback(async (folderId, inviteeEmail, permission = 'viewer') => {
    const folderData = data[folderId] || Object.values(data).find((f) => f._dbId === folderId || f.id === folderId);
    const folderTitle = folderData?.title || 'Shared Folder';
    const realFolderId = folderData?._dbId || folderId;
    await shareFolder({
      folderId: realFolderId, folderTitle,
      ownerEmail: user.email, ownerId: user.id,
      inviteeEmail, permission,
    });
  }, [data, user]);

  const activeItem = displayItems.find((i) => i.id === activeId) ?? null;
  const canMoveOut = currentFolderId !== 'root';
  const canMoveFolderOut = currentFolderId !== 'root';

  if (isLoading) {
    const isDark = settings?.isDark !== false;
    const tc = settings?.themeColors || {};
    const bgColor = isDark ? '#0a0a0a' : '#f5f5f5';
    const spinColor = tc.accent || '#c2ffe0';
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: bgColor }}>
        <ConstellationBg accentColor={spinColor} isDark={isDark} pageBg={bgColor} />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative z-10 flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-4 animate-spin"
            style={{ borderColor: `${spinColor}44`, borderTopColor: spinColor }} />
        </motion.div>
      </div>
    );
  }

  if (role !== 'admin' && role !== 'approved' && role !== 'local' && !isUserAdmin && !user?.isLocal) {
    return <RoleGate role={role} onSignOut={onSignOut} />;
  }

  const isDark = settings.isDark !== false;
  const tc = settings.themeColors;
  const pageBgColor = tc?.pageBg || (isDark ? '#0a0a0a' : '#f0f4f8');

  const sectionLabel = searchQuery
    ? 'Search Results'
    : currentFolderId !== 'root'
      ? currentFolder?.title
      : '';

  const renderBackground = () => {
    if (settings.backgroundGradient) {
      return (
        <div
          className="fixed inset-0 z-0 transition-all duration-700 pointer-events-none"
          style={{
            backgroundColor: settings.customBackground || (isDark ? '#070A09' : '#F8FAFC'),
            backgroundImage: settings.backgroundGradient,
          }}
        />
      );
    }
    if (settings.backgroundType === 'custom' && settings.customBackground) {
      return <div className="fixed inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${settings.customBackground})` }} />;
    }
    if (settings.backgroundType && settings.backgroundType !== 'none') {
      const BgComp = BG_COMPONENTS[settings.backgroundType];
      if (BgComp) return <BgComp accentColor={tc.accent} isDark={isDark} pageBg={pageBgColor} />;
    }
    // High-performance Minimal Zen Ambient Mesh
    return (
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: isDark
            ? 'radial-gradient(circle at 50% -20%, rgba(16, 185, 129, 0.08) 0%, transparent 65%), radial-gradient(circle at 100% 100%, rgba(16, 185, 129, 0.03) 0%, transparent 55%)'
            : 'radial-gradient(circle at 50% -20%, rgba(5, 150, 105, 0.05) 0%, transparent 65%), radial-gradient(circle at 100% 100%, rgba(5, 150, 105, 0.02) 0%, transparent 55%)',
        }}
      />
    );
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="min-h-screen font-sans flex flex-col items-center relative overflow-x-hidden overflow-y-auto w-full"
      style={{ backgroundColor: pageBgColor }}>
      {renderBackground()}

      {/* Minimalist, Clean Top App Bar */}
      <header className="relative z-20 w-full max-w-5xl flex items-center justify-between pt-3 sm:pt-5 pb-2 px-3 sm:px-6 select-none">
        {/* Left: Minimal Brand Badge & Active Enclave */}
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-700 flex items-center justify-center text-white shadow-md shadow-emerald-500/20 shrink-0">
            <SafeIcon icon={FiBookmark} className="w-4 h-4 fill-current" />
          </div>
          <div className="flex items-baseline gap-1.5 sm:gap-2 min-w-0">
            <span className="font-extrabold tracking-tight text-sm font-display text-white shrink-0">MeMark</span>
            <span className="text-[11px] font-mono text-emerald-400/80 max-w-[120px] xs:max-w-[180px] sm:max-w-xs truncate">
              {currentFolderId !== 'root' ? `/ ${currentFolder?.title || ''}` : `/ ${settings.dashboardName || 'My Space'}`}
            </span>
          </div>
        </div>

        {/* Right: Core Quick Action Buttons & Modular Extension Triggers */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Quick Add Bookmark (Primary Action) */}
          <button
            type="button"
            onClick={() => { playSound('click'); setAddBookmarkOpen(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-[0_0_15px_rgba(16,185,129,0.25)] hover:scale-105 active:scale-95"
            style={{
              backgroundColor: tc.accent || '#10B981',
              color: isDark ? '#000000' : '#FFFFFF',
            }}
            title="Add New Bookmark (Ctrl+B)"
          >
            <SafeIcon icon={FiPlus} className="w-3.5 h-3.5 stroke-[2.5]" />
            <span className="hidden xs:inline">Bookmark</span>
          </button>

          {/* Quick Add Folder */}
          <button
            type="button"
            onClick={() => { playSound('click'); setAddFolderOpen(true); }}
            className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer hover:bg-white/5"
            style={{
              borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#CBD5E1',
              color: isDark ? '#E2E8F0' : '#1E293B',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
            }}
            title="Create Subfolder"
          >
            <SafeIcon icon={FiFolder} className="w-3.5 h-3.5 text-amber-400" />
            <span className="hidden xs:inline">Folder</span>
          </button>

          {/* Settings Trigger in Header */}
          <button
            type="button"
            onClick={() => { playSound('click'); setSettingsModalOpen(true); }}
            className="inline-flex items-center justify-center w-8 h-8 rounded-xl border transition-all cursor-pointer hover:scale-105 text-zinc-300 hover:text-white"
            style={{
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#CBD5E1',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
            }}
            title="Open Settings Hub (Alt+,)"
          >
            <SafeIcon icon={FiSettings} className="w-3.5 h-3.5 hover:rotate-90 transition-transform duration-300" />
          </button>

          {/* Admin Direct Trigger in Header */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                playSound('click');
                setSettingsInitialTab('admin');
                setSettingsModalOpen(true);
              }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-[11px] font-semibold transition-all cursor-pointer hover:scale-105 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.15)]"
              title="Open Admin Console"
            >
              <span className="text-xs">👑</span>
              <span className="hidden md:inline font-mono">Admin</span>
            </button>
          )}

          {/* Command Palette Trigger */}
          <button
            type="button"
            onClick={() => { playSound('click'); setCommandPaletteOpen(true); }}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border text-zinc-400 hover:text-white transition-all cursor-pointer"
            style={{
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#CBD5E1',
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
            }}
            title="Open Command Palette (⌘K)"
          >
            <SafeIcon icon={FiCommand} className="w-3 h-3" />
            <span>⌘K</span>
          </button>

          {/* ── MODULAR EXTENSIONS (Rendered ONLY if activated in Settings) ── */}
          {features.circadian && circadianPhase && (
            <div
              className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[10px] font-mono border border-white/10 bg-white/[0.02] text-neutral-400"
              title={`Ambient Chronos: ${circadianPhase.name} - ${circadianPhase.description}`}
            >
              <span>{circadianPhase.icon}</span>
              <span style={{ color: circadianPhase.accent }}>{circadianPhase.name}</span>
            </div>
          )}

          {features.terminal && (
            <button
              type="button"
              onClick={() => { playSound('launch'); setTerminalOpen(true); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all cursor-pointer shadow-sm"
              title="Open Cyber Terminal CLI (~ or `)"
            >
              <SafeIcon icon={FiTerminal} className="w-3 h-3" />
              <span>&gt;_ CLI</span>
            </button>
          )}

          {features.pomodoro && (
            <button
              type="button"
              onClick={() => { playSound('click'); setPomodoroOpen(true); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-all cursor-pointer shadow-sm"
              title="Open Pomodoro Focus Reactor (:pomodoro)"
            >
              <span>⚡ Focus</span>
            </button>
          )}

          {features.lofi && (
            <button
              type="button"
              onClick={() => { playSound('click'); setLofiOpen(true); }}
              className="hidden xl:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-all cursor-pointer shadow-sm"
              title="Open Lo-Fi Cyber Chill Hop Synthesizer (:lofi)"
            >
              <span>☕ Lo-Fi</span>
            </button>
          )}

          {features.binaural && (
            <button
              type="button"
              onClick={() => { playSound('click'); setBinauralOpen(true); }}
              className="hidden xl:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-all cursor-pointer shadow-sm"
              title="Open Binaural Soundscape Generator (:binaural)"
            >
              <span>🎧 Drone</span>
            </button>
          )}

          {features.terrain3d && (
            <button
              type="button"
              onClick={() => { playSound('launch'); setTerrainOpen(true); }}
              className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all cursor-pointer shadow-sm"
              title="Open 3D Topological Knowledge Terrain (:terrain)"
            >
              <span>🏔️ Terrain</span>
            </button>
          )}

          {features.carousel3d && (
            <button
              type="button"
              onClick={() => { playSound('launch'); setCarouselOpen(true); }}
              className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-all cursor-pointer shadow-sm"
              title="Open 3D Holographic Perspective Carousel (Alt+C)"
            >
              <span>🎡 3D</span>
            </button>
          )}

          {features.slideDeck && (
            <button
              type="button"
              onClick={() => {
                playSound('launch');
                setSlideDeckFolder(data[currentFolderId] || Object.values(data)[0] || { name: 'Knowledge Archive', items: filteredDisplayItems });
              }}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-mono border border-sky-500/30 bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 transition-all cursor-pointer shadow-sm"
              title="Launch Slide Deck Presentation (Alt+P)"
            >
              <span>📽️ Slides</span>
            </button>
          )}
        </div>
      </header>

      <main className="relative z-10 w-full max-w-[1040px] px-3 sm:px-6 pt-2 sm:pt-8 pb-36 sm:pb-56 flex flex-col items-center min-h-[85vh]">
        {!zenMode ? (
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} className="mb-3 sm:mb-8 text-center space-y-1 sm:space-y-2">
            <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-mono tracking-widest uppercase border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.15)]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>ENCRYPTED CLOUD SYNC • SOVEREIGN VAULT</span>
            </div>
            <h1 className="text-xl sm:text-5xl font-extrabold select-none drop-shadow-2xl tracking-tight" style={{ color: tc.heading }}>
              {getGreeting()}
            </h1>
            <p className="hidden sm:block text-xs sm:text-sm tracking-wide font-mono opacity-80" style={{ color: tc.muted }}>
              Workspace: <span className="text-emerald-400 font-semibold">{settings.dashboardName}</span> • Cloud Database Synced • BYOK Key: Local Device
            </p>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4 text-center">
            <span className="text-[11px] font-mono tracking-widest uppercase px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
              Zen Focus Active • Press Alt+Z to exit
            </span>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }} className="w-full max-w-lg mb-3 sm:mb-4">
          <div className="relative flex items-center">
            <div className="w-full">
              <GlowInput value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search bookmarks, folders..." accentColor={tc.accent} isDark={isDark} />
            </div>
            <button
              onClick={() => { playSound('click'); setCommandPaletteOpen(true); }}
              title="Open Command Palette (⌘K)"
              className={`hidden sm:flex absolute right-3 px-2 py-1 text-[11px] font-mono rounded-md border transition-all items-center gap-1 shadow-sm ${
                isDark
                  ? 'border-[#333]/40 bg-[#1c1c1c]/70 text-[#aaa] hover:text-white hover:border-[#666]'
                  : 'border-slate-300 bg-slate-100 text-slate-700 hover:text-slate-900 hover:border-slate-400'
              }`}
            >
              <SafeIcon icon={FiCommand} className="w-3 h-3" />
              <span>K</span>
            </button>
          </div>
        </motion.div>

        {/* Quick Access Favorites Dock */}
        <QuickAccessDock
          pinnedBookmarks={pinnedBookmarks}
          onOpenBookmark={handleOpenBookmark}
          onUnpin={handleTogglePin}
          themeColors={tc}
          isDark={isDark}
        />

        {/* Tag Filter Bar */}
        <div className="w-full max-w-2xl mt-2 mb-4">
          <TagFilterBar
            data={data}
            selectedTag={selectedTag}
            onSelectTag={setSelectedTag}
            themeColors={tc}
            isDark={isDark}
          />
        </div>

        <div className="w-full max-w-2xl mt-2">
          <div className="flex flex-wrap items-center justify-between gap-2.5 mb-6 px-1">
            <div className="flex items-center gap-2">
              {sectionLabel && (
                <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: tc.label }}>
                  {sectionLabel}
                </h2>
              )}
              {selectedTag && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-mono bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  Tag: #{selectedTag}
                </span>
              )}
              {isInSharedFolder && (
                <span className="text-[9px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: currentFolderIsEditor ? 'rgba(52,211,153,0.15)' : 'rgba(167,139,250,0.15)',
                    color: currentFolderIsEditor ? '#34d399' : '#a78bfa',
                    border: `1px solid ${currentFolderIsEditor ? '#34d39944' : '#a78bfa44'}`,
                  }}>
                  {currentFolderIsEditor ? '✏ Editor' : '👁 Viewer'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {/* Sort Mode Selector */}
              <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-lg border text-xs shadow-xs"
                style={{ backgroundColor: tc.card, borderColor: tc.border }}>
                <span className="text-[10px] uppercase font-mono opacity-50" style={{ color: tc.muted }}>Sort:</span>
                <select
                  value={sortMode}
                  onChange={(e) => {
                    const next = e.target.value;
                    playSound('click');
                    setSortMode(next);
                    localStorage.setItem('memark_sort_mode', next);
                  }}
                  className="bg-transparent text-xs focus:outline-none cursor-pointer font-medium"
                  style={{ color: tc.text }}
                >
                  <option value="default" className={isDark ? "bg-[#1a1a1a] text-white" : "bg-white text-slate-900"}>Default</option>
                  <option value="clicks" className={isDark ? "bg-[#1a1a1a] text-white" : "bg-white text-slate-900"}>⚡ Most Visited</option>
                  <option value="recent" className={isDark ? "bg-[#1a1a1a] text-white" : "bg-white text-slate-900"}>🕒 Recently Opened</option>
                  <option value="alpha" className={isDark ? "bg-[#1a1a1a] text-white" : "bg-white text-slate-900"}>🔤 Alphabetical</option>
                  <option value="domain" className={isDark ? "bg-[#1a1a1a] text-white" : "bg-white text-slate-900"}>🌐 By Domain</option>
                </select>
              </div>

              {/* View Mode Toggle: Grid, List, Kanban */}
              <div className="flex items-center p-0.5 rounded-lg border mr-0.5 sm:mr-1"
                style={{ backgroundColor: tc.card, borderColor: tc.border }}>
                <button
                  onClick={() => { playSound('click'); setViewMode('grid'); localStorage.setItem('memark_view_mode', 'grid'); }}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'grid'
                      ? isDark ? 'bg-white/10 text-white shadow-xs' : 'bg-slate-200 text-slate-900 shadow-xs font-semibold'
                      : isDark ? 'text-[#888] hover:text-white' : 'text-slate-500 hover:text-slate-900'
                  }`}
                  title="Grid view"
                >
                  <SafeIcon icon={FiGrid} className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { playSound('click'); setViewMode('list'); localStorage.setItem('memark_view_mode', 'list'); }}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'list'
                      ? isDark ? 'bg-white/10 text-white shadow-xs' : 'bg-slate-200 text-slate-900 shadow-xs font-semibold'
                      : isDark ? 'text-[#888] hover:text-white' : 'text-slate-500 hover:text-slate-900'
                  }`}
                  title="List view"
                >
                  <SafeIcon icon={FiList} className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { playSound('click'); setViewMode('kanban'); localStorage.setItem('memark_view_mode', 'kanban'); }}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'kanban'
                      ? isDark ? 'bg-white/10 text-white shadow-xs' : 'bg-slate-200 text-slate-900 shadow-xs font-semibold'
                      : isDark ? 'text-[#888] hover:text-white' : 'text-slate-500 hover:text-slate-900'
                  }`}
                  title="Kanban Board view"
                >
                  <SafeIcon icon={FiColumns} className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Grid Density Switcher (only in grid view, desktop/tablet) */}
              {viewMode === 'grid' && (
                <div className="hidden sm:flex items-center p-0.5 rounded-lg border mr-1 font-mono text-[10px]"
                  style={{ backgroundColor: tc.card, borderColor: tc.border }}>
                  <button
                    onClick={() => { playSound('click'); setDensity('compact'); localStorage.setItem('memark_grid_density', 'compact'); }}
                    className={`px-1.5 py-1 rounded transition-all ${density === 'compact' ? 'bg-emerald-500/20 text-emerald-400 font-bold' : isDark ? 'text-[#888] hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                    title="Compact Micro-Grid Density"
                  >
                    Dense
                  </button>
                  <button
                    onClick={() => { playSound('click'); setDensity('standard'); localStorage.setItem('memark_grid_density', 'standard'); }}
                    className={`px-1.5 py-1 rounded transition-all ${density === 'standard' ? 'bg-emerald-500/20 text-emerald-400 font-bold' : isDark ? 'text-[#888] hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                    title="Standard Balanced Grid Density"
                  >
                    Std
                  </button>
                  <button
                    onClick={() => { playSound('click'); setDensity('cinematic'); localStorage.setItem('memark_grid_density', 'cinematic'); }}
                    className={`px-1.5 py-1 rounded transition-all ${density === 'cinematic' ? 'bg-emerald-500/20 text-emerald-400 font-bold' : isDark ? 'text-[#888] hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                    title="Cinematic Spacious Grid Density"
                  >
                    Cine
                  </button>
                </div>
              )}

              {(currentFolderId !== 'root' || searchQuery) && (
                <button onClick={handleBack}
                  className="text-xs transition-all flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                  style={{ color: tc.muted }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = tc.text; e.currentTarget.style.backgroundColor = tc.card; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = tc.muted; e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <AnimatePresence>
                {saving && (
                  <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}>
                    <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" style={{ color: tc.accent }} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {viewMode === 'kanban' ? (
            <div className="w-full">
              <KanbanBoard
                data={data}
                onOpenBookmark={handleOpenBookmark}
                onItemClick={handleItemClick}
                onContextMenu={handleContextMenu}
                themeColors={tc}
                isDark={isDark}
              />
            </div>
          ) : viewMode === 'list' ? (
            <div className="flex flex-col gap-2">
              {filteredDisplayItems.map((item) => {
                const isFolder = item.type === 'folder';
                const isItemPinned = pinnedIds.includes(item.id);
                const isSelected = selectedItemIds.includes(item.id);
                return (
                  <div
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    className="group flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer hover:border-white/20"
                    style={{
                      backgroundColor: isSelected ? `${tc.accent}15` : tc.card,
                      borderColor: isSelected ? tc.accent : tc.border,
                      color: tc.text,
                    }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleToggleSelect(item); }}
                        className={`p-1 rounded-md transition-all ${
                          isSelected ? 'text-emerald-400 bg-emerald-500/20' : 'text-[#666] hover:text-white'
                        }`}
                        title={isSelected ? 'Deselect item' : 'Select for batch action'}
                      >
                        <SafeIcon icon={isSelected ? FiCheckSquare : FiCheck} className="w-3.5 h-3.5" />
                      </button>

                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }}>
                        {isFolder ? (
                          <SafeIcon icon={FiFolder} className="w-4 h-4 text-amber-400" />
                        ) : item.logoUrl ? (
                          <img src={item.logoUrl} alt="" className="w-5 h-5 rounded object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <SafeIcon icon={FiBookmark} className="w-4 h-4" style={{ color: tc.accent }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate group-hover:text-[#c2ffe0] transition-colors">
                          {item.title}
                        </div>
                        {!isFolder && item.url && (
                          <div className="text-xs truncate opacity-70" style={{ color: tc.muted }}>
                            {item.url.replace(/^https?:\/\//, '')}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setMoveToItem(item)}
                        title="Move to folder across app..."
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-emerald-400 hover:text-emerald-300"
                      >
                        <SafeIcon icon={FiFolder} className="w-3.5 h-3.5" />
                      </button>
                      {!isFolder && (
                        <>
                          <button
                            onClick={() => handleTogglePin(item)}
                            title={isItemPinned ? "Unpin from dock" : "Pin to dock"}
                            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                            style={{ color: isItemPinned ? '#facc15' : tc.muted }}
                          >
                            <SafeIcon icon={FiStar} className={`w-3.5 h-3.5 ${isItemPinned ? 'fill-current' : ''}`} />
                          </button>
                          <button
                            onClick={() => setAiSummaryItem(item)}
                            title="AI Summary"
                            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-purple-400 hover:text-purple-300"
                          >
                            ✨
                          </button>
                          <button
                            onClick={() => handleOpenBookmark(item.url)}
                            title="Open link"
                            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                            style={{ color: tc.muted }}
                          >
                            <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
              onDragStart={handleDragStart} onDragOver={handleDragOver}
              onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
              <SortableContext items={filteredDisplayItems.map((i) => i.id)} strategy={rectSortingStrategy}>
                <div className={`grid ${
                  density === 'compact'
                    ? 'grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2.5'
                    : density === 'cinematic'
                    ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6'
                    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4.5'
                }`}>
                  {filteredDisplayItems.map((item) => (
                    <SortableGridItem key={item.id} item={item}
                      onClick={() => handleItemClick(item)}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      activeItem={activeItem} themeColors={tc}
                      onInfoClick={(it) => setInfoItem(it)}
                      onMoveTo={(it) => setMoveToItem(it)}
                      disabled={item._isShared && item._sharedPermission !== 'editor'}
                      isSelected={selectedItemIds.includes(item.id)}
                      onToggleSelect={handleToggleSelect}
                      hasSelection={selectedItemIds.length > 0}
                      onRssClick={(it) => setRssFeedBookmark(it)}
                      density={density}
                    />
                  ))}
                </div>
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeId && activeItem ? (
                  <div style={{ opacity: 0.95, transform: 'scale(1.05)', cursor: 'grabbing', pointerEvents: 'none', filter: 'drop-shadow(0 15px 25px rgba(0,0,0,0.6))', touchAction: 'none' }}>
                    <GridItem item={activeItem} isDragging={false} themeColors={tc} density={density} />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          {filteredDisplayItems.length === 0 && (
            <div className="py-16 text-center text-sm" style={{ color: tc.muted }}>
              {selectedTag
                ? `No bookmarks matching tag #${selectedTag}`
                : isInSharedFolder && !currentFolderIsEditor
                  ? '👁 This shared folder is empty. You have view-only access.'
                  : isInSharedFolder && currentFolderIsEditor
                    ? '✏ This shared folder is empty. Right-click to add items.'
                    : <><strong>Right-click</strong> anywhere to add items, press <strong>⌘K</strong>, or ask the <strong>AI assistant</strong>!</>}
            </div>
          )}
        </div>

        {/* Batch Floating Action Bar */}
        <BatchActionBar
          selectedItems={selectedItems}
          onClear={() => setSelectedItemIds([])}
          onLaunchAll={handleBatchLaunchAll}
          onExportJSON={handleBatchExport}
          onDeleteSelected={handleBatchDelete}
          onMoveSelected={(items) => setBatchMoveItems(items)}
          themeColors={tc}
        />
      </main>

      {/* Sleek Minimalist Footer Command Dock */}
      <footer className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-auto max-w-[95vw] pointer-events-auto transition-all ${
        selectedItemIds.length > 0 ? 'hidden sm:block' : ''
      }`}>
        <div
          className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-3 py-1.5 rounded-2xl border backdrop-blur-xl shadow-2xl transition-all"
          style={{
            backgroundColor: 'rgba(11, 16, 14, 0.92)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            boxShadow: '0 12px 40px -10px rgba(0, 0, 0, 0.85), 0 0 25px rgba(16, 185, 129, 0.15)',
          }}
        >
          {/* Settings Command Hub Button (Alt+,) */}
          {dockConfig.settings !== false && (
            <button
              onClick={() => { playSound('click'); setSettingsModalOpen(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:scale-105 cursor-pointer group"
              style={{
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                color: '#34d399',
                border: '1px solid rgba(16, 185, 129, 0.35)',
              }}
              title="System Settings Command Hub (⌘, or Alt+,)"
            >
              <SafeIcon icon={FiSettings} className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-300" />
              <span className="hidden sm:inline">Settings</span>
              <kbd className="hidden md:inline text-[9px] font-mono px-1.5 py-0.2 rounded bg-black/40 border border-white/10 opacity-70">
                Alt+,
              </kbd>
            </button>
          )}

          {/* Vault Security (Alt+V) */}
          {features.vaultCrypto && (
            <button
              onClick={() => { playSound('lock'); setVaultModalOpen(true); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium text-emerald-300 hover:bg-white/5 transition-all cursor-pointer"
              title="Zero-Knowledge Vault (Alt+V)"
            >
              <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Vault</span>
            </button>
          )}

          {/* Reading Queue (Alt+R) */}
          {features.readingQueue && (
            <button
              onClick={() => { playSound('click'); setReadingDrawerOpen(true); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-300 hover:bg-white/5 transition-all cursor-pointer"
              title="Reading Queue (Alt+R)"
            >
              <SafeIcon icon={FiBookOpen} className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reading</span>
              {readingListItems.length > 0 && (
                <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  {readingListItems.length}
                </span>
              )}
            </button>
          )}

          {/* Notes */}
          {features.scratchpad && (
            <button
              onClick={() => { playSound('click'); setScratchpadFolder(currentFolder || { id: 'root', title: 'Folder Notes' }); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
              title="Folder Scratchpad Notes"
            >
              <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Notes</span>
            </button>
          )}

          {/* Spatial Mindmap Graph (Alt+G) */}
          {features.graphView && (
            <button
              onClick={() => { playSound('launch'); setGraphModalOpen(true); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-300 hover:bg-white/5 transition-all cursor-pointer"
              title="Spatial Mindmap Constellation Graph (Alt+G)"
            >
              <SafeIcon icon={FiCpu} className="w-3.5 h-3.5 text-cyan-400" />
              <span className="hidden sm:inline">Graph</span>
            </button>
          )}

          {/* Sovereign Scratchpad (Alt+N) */}
          {features.scratchpad && (
            <button
              onClick={() => { playSound('click'); setScratchpadDrawerOpen(true); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium text-slate-300 hover:bg-white/5 transition-all cursor-pointer"
              title="Sovereign Markdown Scratchpad (Alt+N)"
            >
              <SafeIcon icon={FiFileText} className="w-3.5 h-3.5 text-emerald-400" />
              <span className="hidden sm:inline">Scratchpad</span>
            </button>
          )}

          {/* Vertical divider */}
          <div className="h-4 w-px bg-white/15 mx-0.5" />

          {/* Sound Toggle */}
          {features.soundFx && (
            <button
              onClick={handleToggleSound}
              className="p-1.5 rounded-xl text-slate-400 hover:text-emerald-400 hover:bg-white/5 transition-all cursor-pointer"
              title={isMuted ? 'Sound muted (Click to unmute)' : 'Sound enabled (Click to mute)'}
            >
              <SafeIcon icon={isMuted ? FiVolumeX : FiVolume2} className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Zen Focus Mode (Alt+Z) */}
          <button
            onClick={() => { playSound('click'); setZenMode((z) => !z); }}
            className="p-1.5 rounded-xl text-slate-400 hover:text-emerald-400 hover:bg-white/5 transition-all cursor-pointer"
            style={{ color: zenMode ? '#10b981' : undefined }}
            title="Toggle Zen Focus Mode (Alt+Z)"
          >
            <SafeIcon icon={zenMode ? FiMinimize2 : FiMaximize2} className="w-3.5 h-3.5" />
          </button>

          {/* Keyboard Shortcuts (?) */}
          <button
            onClick={() => { playSound('click'); setShortcutsModalOpen(true); }}
            className="hidden sm:inline-flex p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-all cursor-pointer"
            title="Keyboard Shortcuts (?)"
          >
            <SafeIcon icon={FiHelpCircle} className="w-3.5 h-3.5" />
          </button>

          {/* Vertical divider */}
          <div className="h-4 w-px bg-white/15 mx-0.5" />

          {/* Operative Profile Chip & Sign Out */}
          <button
            type="button"
            onClick={() => {
              playSound('click');
              if (window.confirm(`Sign out of MeMark (${user?.email || 'User'})?`)) {
                onSignOut();
              }
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-mono text-slate-300 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-all cursor-pointer"
            title={`Signed in as: ${user?.email || 'User'}. Click to disconnect & sign out`}
          >
            <div className="w-4 h-4 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 flex items-center justify-center text-[9px] font-bold">
              {(user?.name || user?.email || 'M').charAt(0).toUpperCase()}
            </div>
            <span className="hidden sm:inline max-w-[120px] truncate text-[11px]">
              {isUserAdmin ? '👑 Admin' : (user?.isLocal ? 'Local Operative' : (user?.name || user?.email?.split('@')[0] || 'User'))}
            </span>
            <SafeIcon icon={FiLogOut} className="w-3 h-3 opacity-60 hover:opacity-100 ml-0.5" />
          </button>
        </div>
      </footer>

      {features.aiAssistant && (
        <AIButton isOpen={aiOpen} onClick={() => setAiOpen((v) => !v)} accentColor={tc.accent} themeColors={tc} />
      )}

      <div data-ai>
        <AIAssistant
          isOpen={aiOpen} onClose={() => setAiOpen(false)}
          themeColors={tc} data={data}
          addBookmark={addBookmark} addFolder={addFolder}
          editBookmark={editBookmark} editFolder={editFolder}
          removeBookmark={removeBookmark} removeFolder={removeFolder}
          moveBookmarkToFolder={moveBookmarkToFolder}
          moveFolderToFolder={moveFolderToFolder}
          currentFolderId={currentFolderId}
          onShareFolder={handleAIShareFolder}
          settings={settings}
          onSettingsChange={persistSettings}
          isDark={isDark}
          onOpenSettings={(tab) => { setSettingsInitialTab(tab || 'ai'); setSettingsModalOpen(true); }}
        />
      </div>

      <div data-info>
        <BookmarkInfoPanel
          item={infoItem}
          allItems={Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link')}
          onOpenBookmark={handleOpenBookmark}
          themeColors={tc}
          isDark={isDark}
          onSave={handleInfoSave}
          onClose={() => setInfoItem(null)}
          onMoveTo={(it) => setMoveToItem(it)}
          onAISummary={(it) => setAiSummaryItem(it)}
          onOpenCodeRunner={(code, title, lang) => setCodeRunnerState({ isOpen: true, code, title, language: lang || 'javascript' })}
          onOpenTotp={(bm) => setTotpBookmark(bm)}
          onOpenPdf={(bm) => setPdfViewerBookmark(bm)}
          userId={user?.id || user?.email || 'default_user'}
        />
      </div>

      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          item={ctxMenu.item}
          onEdit={handleCtxEdit}
          onDelete={handleCtxDelete}
          onAddBookmark={() => { if (canMutateHere) setAddBookmarkOpen(true); }}
          onAddFolder={() => { if (canMutateHere) setAddFolderOpen(true); }}
          onMoveOut={handleCtxMoveOut}
          onMoveFolderOut={handleCtxMoveFolderOut}
          onMoveTo={(it) => setMoveToItem(it)}
          onShare={handleCtxShare}
          onLeaveFolder={handleCtxLeaveFolder}
          canMoveOut={canMoveOut}
          canMoveFolderOut={canMoveFolderOut}
          onClose={closeCtxMenu}
          themeColors={tc}
          isDark={isDark}
          onSettings={() => { setSettingsInitialTab('general'); setSettingsModalOpen(true); }}
          onSignOut={onSignOut}
          isAdmin={isAdmin}
          onAdmin={() => { setSettingsInitialTab('admin'); setSettingsModalOpen(true); }}
          canAdd={canMutateHere}
          onAISummary={(it) => setAiSummaryItem(it)}
          onQRCode={(it) => setQrCodeItem(it)}
          onTogglePin={handleTogglePin}
          isPinned={pinnedIds.includes(ctxMenu?.item?.id)}
          onOpenFolder={(f) => handleNavigate(f.targetId || f._dbFolderId)}
          isSelected={ctxMenu.item ? selectedItemIds.includes(ctxMenu.item.id) : false}
          onToggleSelect={handleToggleSelect}
          onSelectAll={handleSelectAll}
          selectedCount={selectedItemIds.length}
          onClearSelection={() => setSelectedItemIds([])}
        />
      )}

      <div data-modal>
        <MoveToModal
          isOpen={!!moveToItem || batchMoveItems.length > 0}
          onClose={() => { setMoveToItem(null); setBatchMoveItems([]); }}
          item={moveToItem}
          items={batchMoveItems}
          currentFolderId={currentFolderId}
          data={data}
          onMove={handleConfirmMoveTo}
          themeColors={tc}
          isDark={isDark}
        />
        <EditModal
          isOpen={editModalOpen} onClose={() => setEditModalOpen(false)}
          item={editingItem} onSave={handleSaveEdit}
          onDelete={handleDeleteItem} isNew={false} themeColors={tc}
          userId={user?.id || user?.email || 'default_user'}
          isDark={isDark}
        />
        <AddBookmarkModal
          isOpen={addBookmarkOpen} onClose={() => setAddBookmarkOpen(false)}
          onSave={handleSaveBookmark} themeColors={tc}
          userId={user?.id || user?.email || 'default_user'}
          isDark={isDark}
        />
        <AddFolderModal
          isOpen={addFolderOpen} onClose={() => setAddFolderOpen(false)}
          onSave={handleSaveFolder} themeColors={tc}
          isDark={isDark}
        />
        <SettingsModal
          isOpen={settingsModalOpen} onClose={() => setSettingsModalOpen(false)}
          settings={settings} onSettingsChange={persistSettings}
          data={data} onImportBookmarks={handleImportBookmarks}
          user={user} onSignOut={onSignOut}
          isAdmin={isAdmin}
          initialTab={settingsInitialTab}
        />
        <ShareFolderModal
          isOpen={!!shareFolderModal} onClose={() => setShareFolderModal(null)}
          folder={shareFolderModal} themeColors={tc}
          onLeaveFolder={handleLeaveFolder}
          isDark={isDark}
        />
        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          data={data}
          onNavigateFolder={(id) => { setCurrentFolderId(id); setSearchQuery(''); }}
          onOpenBookmark={handleOpenBookmark}
          onAddBookmark={() => { if (canMutateHere) setAddBookmarkOpen(true); }}
          onAddFolder={() => { if (canMutateHere) setAddFolderOpen(true); }}
          onOpenSettings={() => setSettingsModalOpen(true)}
          onOpenAI={() => setAiOpen(true)}
          onOpenAudit={() => setAuditModalOpen(true)}
          onToggleViewMode={toggleViewMode}
          onOpenVault={() => setVaultModalOpen(true)}
          onOpenReading={() => setReadingDrawerOpen(true)}
          onToggleZen={() => setZenMode((z) => !z)}
          onOpenShortcuts={() => setShortcutsModalOpen(true)}
          themeColors={tc}
          isDark={isDark}
        />
        <CyberTerminalModal
          isOpen={terminalOpen}
          onClose={() => setTerminalOpen(false)}
          data={data}
          onSearch={(term) => setSearchQuery(term)}
          onOpenBookmark={handleOpenBookmark}
          onSelectTag={(t) => setSelectedTag(t)}
          onToggleZen={() => setZenMode((z) => !z)}
          onOpenVault={() => setVaultModalOpen(true)}
          onOpenReading={() => setReadingDrawerOpen(true)}
          onOpenGraph={() => setGraphModalOpen(true)}
          onOpenScratchpad={() => setScratchpadDrawerOpen(true)}
          onOpenAudit={() => setAuditModalOpen(true)}
          onExportObsidian={() => {
            const allItems = Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link');
            const folders = Object.values(data).map(f => ({ id: f.id, name: f.name || f.title || 'Folder' }));
            downloadObsidianVault(allItems, folders);
          }}
          onExportHtml={() => {
            const allItems = Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link');
            downloadStandaloneVault(allItems, 'MeMark Sovereign Vault');
          }}
          onSetDensity={(mode) => setDensity(mode)}
          onOpenCodeRunner={(code, title, lang) => setCodeRunnerState({ isOpen: true, code, title: title || 'Terminal Sandbox', language: lang || 'javascript' })}
          onArchiveStale={handleArchiveStale}
          onOpenHeatmap={() => setHeatmapOpen(true)}
          onOpenTotp={() => setTotpBookmark(infoItem || Object.values(data).flatMap(f => f.items || [])[0])}
          onOpenRegexRules={() => setRegexRulesOpen(true)}
          onOpenPdfViewer={(bm) => setPdfViewerBookmark(bm || Object.values(data).flatMap(f => f.items || []).find(i => isPdfUrl(i.url)) || Object.values(data).flatMap(f => f.items || [])[0])}
          onOpenSnapshot={() => setSnapshotModalOpen(true)}
          themeColors={tc}
        />
        <GraphViewModal
          isOpen={graphModalOpen}
          onClose={() => setGraphModalOpen(false)}
          items={Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link')}
          folders={Object.values(data).map(f => ({ id: f.id, name: f.name || f.title || 'Folder' }))}
          folderColors={folderColors}
        />
        <ScratchpadDrawer
          isOpen={scratchpadDrawerOpen}
          onClose={() => setScratchpadDrawerOpen(false)}
          currentBookmarks={filteredDisplayItems.filter(i => i.type === 'link')}
          isDark={isDark}
        />
        <AISummaryModal
          isOpen={!!aiSummaryItem}
          onClose={() => setAiSummaryItem(null)}
          item={aiSummaryItem}
          onSaveNote={handleInfoSave}
          themeColors={tc}
          isDark={isDark}
          onOpenSettings={(tab) => { setSettingsInitialTab(tab || 'ai'); setSettingsModalOpen(true); }}
        />
        <QRCodeModal
          isOpen={!!qrCodeItem}
          onClose={() => setQrCodeItem(null)}
          item={qrCodeItem}
          bookmark={qrCodeItem}
          themeColors={tc}
          isDark={isDark}
        />
        <AuditModal
          isOpen={auditModalOpen}
          onClose={() => setAuditModalOpen(false)}
          data={data}
          onDeleteBookmark={removeBookmark}
          onFixBookmark={editBookmark}
          themeColors={tc}
          isDark={isDark}
        />
        <VaultSecurityModal
          isOpen={vaultModalOpen}
          onClose={() => setVaultModalOpen(false)}
          data={data}
          userId={user?.id || user?.email || 'default_user'}
          onSaveBookmark={editBookmark}
          themeColors={tc}
          isDark={isDark}
        />
        <ReadingListDrawer
          isOpen={readingDrawerOpen}
          onClose={() => setReadingDrawerOpen(false)}
          items={readingListItems}
          onOpenBookmark={handleOpenBookmark}
          themeColors={tc}
          isDark={isDark}
        />
        <KeyboardShortcutsModal
          isOpen={shortcutsModalOpen}
          onClose={() => setShortcutsModalOpen(false)}
          themeColors={tc}
          isDark={isDark}
        />
        <FolderScratchpadModal
          isOpen={!!scratchpadFolder}
          onClose={() => setScratchpadFolder(null)}
          folder={scratchpadFolder}
          themeColors={tc}
          isDark={isDark}
        />
        <CodeRunnerModal
          isOpen={codeRunnerState.isOpen}
          onClose={() => setCodeRunnerState(prev => ({ ...prev, isOpen: false }))}
          initialCode={codeRunnerState.code}
          title={codeRunnerState.title}
          language={codeRunnerState.language}
        />
        <RssFeedDrawer
          isOpen={!!rssFeedBookmark}
          onClose={() => setRssFeedBookmark(null)}
          feedBookmark={rssFeedBookmark}
          themeColors={tc}
          isDark={isDark}
        />
        <TotpModal
          isOpen={!!totpBookmark}
          onClose={() => setTotpBookmark(null)}
          bookmark={totpBookmark}
          onSaveSecret={(id, secret) => editBookmark(id, { totpSecret: secret })}
          themeColors={tc}
        />
        <ActivityHeatmapModal
          isOpen={heatmapOpen}
          onClose={() => setHeatmapOpen(false)}
          data={data}
          themeColors={tc}
        />
        <RegexRulesModal
          isOpen={regexRulesOpen}
          onClose={() => setRegexRulesOpen(false)}
          bookmarks={Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link')}
          folders={Object.values(data).map(f => ({ id: f.id, name: f.name || f.title || 'Folder' }))}
          onUpdateBookmarks={async (updated) => {
            for (const b of updated) {
              await editBookmark(b.id, b);
            }
          }}
        />
        <PdfViewerModal
          isOpen={!!pdfViewerBookmark}
          onClose={() => setPdfViewerBookmark(null)}
          bookmark={pdfViewerBookmark}
          onUpdateBookmark={(bm) => editBookmark(bm.id, bm)}
        />
        <SessionSnapshotModal
          isOpen={snapshotModalOpen}
          onClose={() => setSnapshotModalOpen(false)}
          bookmarks={Object.values(data).flatMap(f => f.items || []).filter(i => i.type === 'link')}
          folders={Object.values(data).map(f => ({ id: f.id, name: f.name || f.title || 'Folder' }))}
          onRestoreSnapshot={(snap) => {
            if (snap.bookmarks && snap.bookmarks.length > 0) {
              snap.bookmarks.slice(0, 5).forEach(b => {
                if (b.url) window.open(b.url, '_blank');
              });
            }
          }}
        />
      </div>

      {isAdmin && (
        <AdminPanel isOpen={adminPanelOpen} onClose={() => setAdminPanelOpen(false)} themeColors={tc} />
      )}
    </div>
  );
}

const LOCAL_USER = {
  id: 'local_user',
  name: 'Local Operative',
  email: 'sovereign@device',
  isLocal: true,
};

export default function App() {
  const [user, setUser] = useState(() => {
    return getSession();
  });
  const [role, setRole] = useState(() => {
    const s = getSession();
    if (s?.email?.toLowerCase() === 'y.yang.priv@gmail.com') return 'admin';
    if (s?.isLocal) return 'local';
    if (s) return 'approved';
    return null;
  });

  const handleAuth = (authUser, authRole) => {
    setUser(authUser);
    setRole(authRole || (authUser?.email?.toLowerCase() === 'y.yang.priv@gmail.com' ? 'admin' : 'approved'));
  };

  const handleContinueAsGuest = () => {
    setUser(LOCAL_USER);
    setRole('local');
  };

  const handleSignOut = () => {
    signOut();
    localStorage.removeItem('ncb_token');
    localStorage.removeItem('ncb_user');
    setUser(null);
    setRole(null);
  };

  if (!user) {
    return <AuthPage onAuth={handleAuth} onContinueAsGuest={handleContinueAsGuest} />;
  }

  return <Dashboard user={user} initialRole={role} onSignOut={handleSignOut} />;
}